import React, { useMemo } from "react";
import { Alert, Tag, Tour, Typography } from "antd";

const target = name => () => document.querySelector(`[data-handover-tour="${name}"]`);

export function OperationsHandoverTour({ open, onClose, paymentVisible, status }) {
  const steps = useMemo(() => {
    const result = [{
      title: "From Sales to Operations",
      description: "This workflow transfers an accepted commercial order to Operations without losing its invoice, delivery, or authorization context.",
      target: target("workflow"), placement: "center",
    }];

    if (paymentVisible) result.push({
      title: "1. Establish the financial position",
      description: <div className="handover-tour-copy"><Typography.Paragraph>Open Payments V2 to preview and lock the customer-confirmed invoice. Then record real receipts or an approved settlement arrangement.</Typography.Paragraph><Tag color="blue">A payment request is not money received</Tag></div>,
      target: target("payments"), placement: "right",
    });

    result.push(
      {
        title: "The server controls readiness",
        description: <div className="handover-tour-copy"><Typography.Paragraph>This relationship indicator reports the next blocking requirement from the backend. The frontend does not infer eligibility.</Typography.Paragraph>{status?.reason && <Alert type={status.eligible ? "success" : "info"} showIcon message={status.reason} />}</div>,
        target: target("readiness"), placement: "right",
      },
      {
        title: "Review invoice and payment coverage",
        description: "Invoice, received, and due amounts come from Payments V2. Fully paid and authorized for fulfillment are different outcomes.",
        target: target("financial-summary"), placement: "left",
      },
      {
        title: "Choose delivery instructions per order line",
        description: "Every quoted part must have a saved customer delivery address. Select all addresses, then save them before continuing.",
        target: target("addresses"), placement: "left",
      },
    );

    if (status?.state === "awaiting_approval") {
      result.push({
        title: "Management approval is pending",
        description: "Commercial inputs remain locked while the request is reviewed. Once approved, an authorized user applies the handover from this section.",
        target: target("approval"), placement: "left",
      });
    } else if (status?.state === "authorized") {
      result.push({
        title: "Responsibility has transferred",
        description: "The Operations order is now authoritative for procurement, custody, logistics, exceptions, warranty, and its remaining financial obligations.",
        target: target("authorized"), placement: "left",
      });
    } else {
      result.push({
        title: "Record the authorization basis",
        description: <div className="handover-tour-copy"><Typography.Paragraph>Select only a basis returned by the server. Add terms when COD, pay-later, or an exceptional approval needs operational context.</Typography.Paragraph><Tag color="gold">Permission does not override workflow eligibility</Tag></div>,
        target: target("authorization"), placement: "left",
      });
      result.push({
        title: "Hand over once every dependency is complete",
        description: "Submission is idempotent. It either creates the Operations order directly or starts the required management-approval workflow.",
        target: target("submit"), placement: "top",
      });
    }

    return result;
  }, [paymentVisible, status]);

  return <Tour open={open} onClose={onClose} steps={steps} arrow mask={{ color: "rgba(5, 12, 24, 0.58)" }} scrollIntoViewOptions={{ behavior: "smooth", block: "center" }} type="primary" />;
}
